import { PageHeader, Table, Tag } from 'antd'
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { TABLE_MAX_HEIGHT } from '@/constants/customer'
import { Tank, TankHistory, TankMethodType } from '@/types'
import { formatHm, formatYMD } from '@/utils/date'
import { formatJapaneseNumber } from '@/utils/formatPrice'

import { Container, CustomRowTable } from './styles'

type Props = {
  tankId: number
  dataSource: TankHistory[] | undefined
  dataSourceDetail: Tank[]
  getTankHistoriesLoading: boolean
  page: number
  pageSize: number
  totalItems: number
  onChange: (config: TablePaginationConfig) => void
}

const TankHistoriesTable = ({
  dataSource,
  dataSourceDetail,
  getTankHistoriesLoading,
  page,
  pageSize,
  totalItems,
  onChange,
}: Props) => {
  const { t } = useTranslation()

  const renderMethod = (value: TankMethodType) => {
    switch (value) {
      case TankMethodType.MANUAL:
        return <Tag color="magenta">{t('components.pages.tanks.list.table.manual')}</Tag>
      case TankMethodType.AUTOMATIC:
        return <Tag color="lime">{t('components.pages.tanks.list.table.automatic')}</Tag>
      default:
        null
    }
  }
  const tankColumns: ColumnsType<Tank> = [
    {
      title: t('components.pages.tanks.list.table.tankNo'),
      dataIndex: 'tankNo',
      key: 'tankNo',
      fixed: 'left',
      render: (record: string) => {
        return <CustomRowTable type="right">{record}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap',
    },
    {
      title: t('components.pages.tanks.list.table.detergentName'),
      render: (record: Tank) => {
        return <CustomRowTable type="left">{record.detergents[0] ? record.detergents[0].name : ''}</CustomRowTable>
      },
      fixed: 'left',
      align: 'center' as const,
      className: 'nowrap-tank',
    },
    {
      title: t('components.pages.tanks.list.table.method'),
      dataIndex: 'method',
      key: 'method',
      render: (record: TankMethodType) => {
        return <CustomRowTable type="left">{renderMethod(record)}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-tank',
    },
    {
      title: t('components.pages.tanks.list.table.netWeightMl'),
      render: (record: Tank) => {
        return (
          <CustomRowTable type="right">
            {record.detergents[0] ? formatJapaneseNumber(record.detergents[0].netWeightMl) : ''}
          </CustomRowTable>
        )
      },
      align: 'center' as const,
      className: 'nowrap-tank',
    },
    {
      title: t('components.pages.tanks.list.table.thresholdQuantity'),
      dataIndex: 'thresholdQuantity',
      key: 'thresholdQuantity',
      render: (record: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(record)}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-tank',
    },
    {
      title: t('components.pages.tanks.list.table.remainingQuantity'),
      dataIndex: 'remainingQuantity',
      key: 'remainingQuantity',
      render: (record: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(record)}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-tank',
    },
    {
      title: t('components.pages.tanks.list.table.usedQuantity'),
      dataIndex: 'usedQuantity',
      key: 'usedQuantity',
      render: (record: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(record)}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-tank',
    },
  ]

  const columns: ColumnsType<TankHistory> = [
    {
      title: t('components.pages.tankHistories.list.table.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      render: (record: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(record)}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.tanks.list.table.netWeightMl'),
      dataIndex: 'caseMl',
      key: 'caseMl',
      render: (record: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(record)}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.tankHistories.list.table.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (record: string) => {
        return (
          <CustomRowTable type="left">
            {formatYMD(record)} {formatHm(record)}
          </CustomRowTable>
        )
      },
      align: 'center' as const,
    },
  ]

  return (
    <Container>
      <Table
        rowKey="id"
        bordered
        dataSource={dataSourceDetail}
        columns={tankColumns}
        pagination={false}
        scroll={{ x: 'max-content', y: TABLE_MAX_HEIGHT }}
      />
      <div>
        <PageHeader ghost={false} title={`${t('components.pages.tankHistories.list.pageHeader.tankTitle')}`} />
        <Table
          rowKey="id"
          bordered
          dataSource={dataSource}
          columns={columns}
          loading={getTankHistoriesLoading}
          pagination={{
            pageSize,
            current: page,
            total: totalItems,
            showSizeChanger: false,
          }}
          onChange={onChange}
          scroll={{ x: 'max-content', y: TABLE_MAX_HEIGHT }}
        />
      </div>
    </Container>
  )
}

TankHistoriesTable.displayName = 'TankHistoriesTable'
export default TankHistoriesTable
